/**
 * v3.admin.compliance — typed admin WRITE surface for the compliance config
 * cluster (ComplianceGates / ProcedureCitations / GateKeys / EntityRules).
 *
 * Mirrors the v3-admin-products.ts pattern: each catalog gets a typed
 * upsert/remove sub-client; composed onto `AdminApi.compliance` from
 * client.ts after construction (cast, per the module augmentation below).
 *
 * Routes (server: PCBJxChangeApi/Functions/AdminComplianceFunction.cs,
 * RequiredRole = "Risk"):
 *   POST   /v3/admin/compliance-gates
 *   DELETE /v3/admin/compliance-gates/{gateCode}
 *   POST   /v3/admin/procedure-citations
 *   DELETE /v3/admin/procedure-citations/{citationKey}
 *   POST   /v3/admin/gate-keys
 *   DELETE /v3/admin/gate-keys/{key}
 *   POST   /v3/admin/entity-rules
 *   DELETE /v3/admin/entity-rules/{customerType}
 *
 * Auth: first positional `bypassKey` arg routed through buildAdminAuthHeaders.
 * Pass '' to omit the bypass header and authorize via the bearer role-claim
 * (Wave 13). Shapes verified against Models/AdminComplianceModels.cs
 * (2026-06-14).
 */

import type { RequestOptions } from '@retail-ops/shared-types';
import { buildAdminAuthHeaders } from '../admin-auth.js';
import type { ApiHttpClient } from '../http.js';
import type { AdminWriteResult } from './admin.js';

// ─────────────────────────────────────────────────────────────────────────
// Body shapes — camelCase JSON wire format consumed by the Function layer.
// ─────────────────────────────────────────────────────────────────────────

export interface ComplianceGateUpsertBody {
  gateCode: string;
  displayName: string;
  description?: string | null;
  category?: string | null;
  isHardGate?: boolean;
  regulatoryBasis?: string | null;
  coreFields?: string | null;
  coreAction?: string | null;
  sortOrder?: number | null;
  isActive?: boolean;
}

export interface ProcedureCitationUpsertBody {
  citationKey: string;
  citationType: string;        // memory-file | pdf-page | form-id | collette-confirm | aurora-export | bill-directive | scott-bond-ticket | collette-pcb-form | external-reg
  displayLabel: string;
  sourcePath?: string | null;
  pageReference?: string | null;
  author?: string | null;
  notes?: string | null;
  sourceDate?: string | null;  // ISO date
  isActive?: boolean;
}

export interface GateKeyUpsertBody {
  key: string;
  displayName: string;
  category?: string | null;    // BSA | CIP | Risk | Product | Workflow | Disclosure | Signing | DocCapture | Other
  description?: string | null;
  scope?: string | null;       // subtype | product | role | global
  allowedStrategiesJson?: string | null;  // JSON array
  defaultStrategy?: string | null;
  defaultDeferralDays?: number | null;
  citationKey?: string | null; // FK → ProcedureCitations.CitationKey
  sortOrder?: number | null;
  isActive?: boolean;
}

export interface EntityRuleUpsertBody {
  customerType: string;
  displayName: string;
  description?: string | null;
  callClassCode?: string | null;
  custCode?: string | null;
  tinCodeDefault?: string | null;
  consumerFlag?: boolean;
  protectedPerson?: boolean;
  businessOnly?: boolean;
  personalOnly?: boolean;
  phase?: string | null;
  cifMessageTemplate?: string | null;
  nameFormat?: string | null;
  accountTitleFormat?: string | null;
  helpText?: string | null;
  specialInstructions?: string | null;
  openingNotes?: string | null;
  sortOrder?: number | null;
  isActive?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────
// Sub-clients
// ─────────────────────────────────────────────────────────────────────────

class ComplianceGatesAdminApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public upsert(bypassKey: string, body: ComplianceGateUpsertBody, opts?: RequestOptions): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.post<AdminWriteResult>('/v3/admin/compliance-gates', { ...opts, headers, body });
  }

  public remove(bypassKey: string, gateCode: string, opts?: RequestOptions): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.del<AdminWriteResult>(`/v3/admin/compliance-gates/${encodeURIComponent(gateCode)}`, { ...opts, headers });
  }
}

class ProcedureCitationsAdminApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public upsert(bypassKey: string, body: ProcedureCitationUpsertBody, opts?: RequestOptions): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.post<AdminWriteResult>('/v3/admin/procedure-citations', { ...opts, headers, body });
  }

  public remove(bypassKey: string, citationKey: string, opts?: RequestOptions): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.del<AdminWriteResult>(`/v3/admin/procedure-citations/${encodeURIComponent(citationKey)}`, { ...opts, headers });
  }
}

class GateKeysAdminApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public upsert(bypassKey: string, body: GateKeyUpsertBody, opts?: RequestOptions): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.post<AdminWriteResult>('/v3/admin/gate-keys', { ...opts, headers, body });
  }

  public remove(bypassKey: string, key: string, opts?: RequestOptions): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.del<AdminWriteResult>(`/v3/admin/gate-keys/${encodeURIComponent(key)}`, { ...opts, headers });
  }
}

class EntityRulesAdminApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public upsert(bypassKey: string, body: EntityRuleUpsertBody, opts?: RequestOptions): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.post<AdminWriteResult>('/v3/admin/entity-rules', { ...opts, headers, body });
  }

  public remove(bypassKey: string, customerType: string, opts?: RequestOptions): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.del<AdminWriteResult>(`/v3/admin/entity-rules/${encodeURIComponent(customerType)}`, { ...opts, headers });
  }
}

/**
 * Composite — exposed at `client.retail.admin.compliance`. One sub-client per
 * compliance config catalog.
 */
export class AdminComplianceApi {
  public readonly complianceGates: ComplianceGatesAdminApi;
  public readonly procedureCitations: ProcedureCitationsAdminApi;
  public readonly gateKeys: GateKeysAdminApi;
  public readonly entityRules: EntityRulesAdminApi;

  public constructor(http: ApiHttpClient) {
    this.complianceGates = new ComplianceGatesAdminApi(http);
    this.procedureCitations = new ProcedureCitationsAdminApi(http);
    this.gateKeys = new GateKeysAdminApi(http);
    this.entityRules = new EntityRulesAdminApi(http);
  }
}

// ─────────────────────────────────────────────────────────────────────────
// Module augmentation — extend AdminApi with `.compliance`.
// ─────────────────────────────────────────────────────────────────────────

declare module './admin.js' {
  // eslint-disable-next-line @typescript-eslint/no-shadow
  interface AdminApi {
    /** Compliance config typed write surface (gates / citations / gate-keys / entity-rules). */
    readonly compliance: AdminComplianceApi;
  }
}
